import { forwardRef, useCallback, useMemo } from 'react';

import { ClickAwayListener } from '@mui/material';

import { useAbility } from '@cast/shared-ability-data-access';

import {
  Drawer,
  DrawerProps,
  Box,
  IconButton,
  Icons,
  Tooltip,
} from '@cast/design-system';

import { DrawerSplitLayout } from './DrawerSplitLayout';
import {
  RestrictionsLoadingSkeleton,
  YamlPreviewLoadingSkeleton,
} from './LoadingSkeletons';
import { RestrictionsDetailsSection } from './RestrictionsDetailsSection';
import { YamlPreviewSection } from './YamlPreviewSection';
import { useSuppressConfirmationDialog } from '../_hooks/useSuppressConfirmationDialog';
import { buildLineRange, isItemClickable } from '../_utils/selectionUtils';
import type {
  ItemClickHandler,
  LineClickHandler,
  RestrictionCategory,
  YamlFile,
} from '../types';

type WorkloadDrawerViewProps = {
  resourceName: string;
  categories: RestrictionCategory[];
  yamlFiles: YamlFile[];
  selectedItemId?: string;
  activeManifestId?: string;
  highlightedLines?: number[];
  isLoading: boolean;
  onItemClick: ItemClickHandler;
  onLineClick?: LineClickHandler;
  onSuppressWorkload: () => void;
  onSuppressRestrictionType: (restrictionType: string) => void;
} & Omit<DrawerProps, 'children'>;

export const WorkloadDrawerView = forwardRef<
  HTMLDivElement,
  WorkloadDrawerViewProps
>(
  ({
    resourceName,
    categories,
    yamlFiles,
    selectedItemId,
    activeManifestId,
    highlightedLines,
    isLoading,
    onItemClick,
    onLineClick,
    onSuppressWorkload,
    onSuppressRestrictionType,
    ...drawerProps
  }: WorkloadDrawerViewProps) => {
    const ability = useAbility();
    const { openDialog: openConfirmationDialog } =
      useSuppressConfirmationDialog();

    const handleSuppressWorkloadClick = useCallback(() => {
      openConfirmationDialog({
        testId: 'suppress-workload-confirmation-dialog',
        title: 'Suppress workload',
        message: 'Are you sure you want to suppress this workload?',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        onConfirm: onSuppressWorkload,
      });
    }, [openConfirmationDialog, onSuppressWorkload]);
    const handleClickAway = useCallback(
      (event: MouseEvent | TouchEvent) => {
        drawerProps.onClose?.(event, 'backdropClick');
      },
      [drawerProps]
    );

    const categoriesWithHandlers = useMemo(
      () =>
        categories.map((category) => ({
          ...category,
          items: category.items.map((item) => ({
            ...item,
            onClick: () => {
              if (!isItemClickable(item)) {
                return;
              }
              onItemClick(
                item.itemId || '',
                item.manifestId,
                buildLineRange(item.startLine, item.endLine)
              );
            },
          })),
        })),
      [categories, onItemClick]
    );

    const renderLeftSideContent = isLoading
      ? () => <RestrictionsLoadingSkeleton />
      : () => (
          <RestrictionsDetailsSection
            restrictions={categoriesWithHandlers}
            selectedItemId={selectedItemId}
            onSuppressRestrictionType={onSuppressRestrictionType}
          />
        );

    const renderRightSideContent = isLoading
      ? () => <YamlPreviewLoadingSkeleton />
      : () => (
          <YamlPreviewSection
            yamlFiles={yamlFiles}
            activeManifestId={activeManifestId}
            highlightedLines={highlightedLines}
            onLineClick={onLineClick}
          />
        );

    return (
      <Drawer size="xxlarge" testId="restrictions-drawer" {...drawerProps}>
        <ClickAwayListener onClickAway={handleClickAway}>
          <Box height="100%">
            <DrawerSplitLayout
              header="Restrictions in workload:"
              title={resourceName}
              titleAction={
                ability.can(
                  'view',
                  'FeatureOptimizationConstraintsSilencing'
                ) ? (
                  <Tooltip title="Suppress workload">
                    <IconButton
                      variant="ghost"
                      size="md"
                      aria-label="Suppress workload"
                      testId="suppress-workload-button"
                      onClick={handleSuppressWorkloadClick}
                    >
                      <Icons.BellSlash size={20} />
                    </IconButton>
                  </Tooltip>
                ) : undefined
              }
              renderLeftSide={renderLeftSideContent}
              renderRightSide={renderRightSideContent}
            />
          </Box>
        </ClickAwayListener>
      </Drawer>
    );
  }
);

WorkloadDrawerView.displayName = 'WorkloadDrawerView';
